import Decimal from "decimal.js";
import type { QuotePriceRounding, RoundingMode } from "../schemas/quotes";

/**
 * Maps a rounding mode to its decimal.js rounding constant. UP and DOWN are
 * directional (numeric ceiling/floor), not magnitude-based, so a negative
 * credit-line price moves the same way the UI labels imply: UP @ 1 turns
 * -12.10 into -12 and DOWN @ 1 turns it into -13.
 */
const MODES: Record<RoundingMode, Decimal.Rounding> = {
  UP: Decimal.ROUND_CEIL,
  DOWN: Decimal.ROUND_FLOOR,
  NEAREST: Decimal.ROUND_HALF_UP,
};

/**
 * Rounds a price to the configured increment and direction. Falls back to the
 * unrounded value when the configured increment is not a positive decimal, so a
 * bad persisted config can never make quote rendering throw.
 * @param value - decimal string in the display currency
 * @param config - the org's quote price rounding config
 * @returns rounded decimal string
 */
export const roundQuotePrice = (
  value: string,
  config: QuotePriceRounding,
): string => {
  const increment = parseIncrement(config.increment);
  if (!increment) {
    return value;
  }
  return new Decimal(value).toNearest(increment, MODES[config.mode]).toString();
};

/**
 * Parses a rounding increment, accepting only finite positive decimals.
 * @param increment - the configured increment string
 * @returns the parsed increment, or null when invalid
 */
const parseIncrement = (increment: string): Decimal | null => {
  try {
    const parsed = new Decimal(increment);
    return parsed.isFinite() && parsed.greaterThan(0) ? parsed : null;
  } catch {
    return null;
  }
};
